import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerField } from "@/components/shared/DatePickerField";
import type { FiscalProfileEditorValues } from "@/lib/fiscalProfileEditorTypes";
import type { FiscalCompanyType } from "@/services/fiscalService";

const COMPANY_TYPES: FiscalCompanyType[] = ["unipersonal", "sas", "srl", "sa"];
const COMPANY_TYPE_KEYS: Record<FiscalCompanyType, string> = {
  unipersonal: "companyTypeUnipersonal",
  sas: "companyTypeSas",
  srl: "companyTypeSrl",
  sa: "companyTypeSa",
};

export interface CompanyIdentitySectionProps {
  idPrefix: string;
  hideCompanyType?: boolean;
  /** When set, show read-only industry line (onboarding industry). */
  industryCode?: string | null;
  /** Defaults to 'owner'. 'accountant' swaps "tu" copy for third-person. */
  audience?: "owner" | "accountant";
}

export function CompanyIdentitySection({
  idPrefix,
  hideCompanyType = false,
  industryCode,
  audience = "owner",
}: CompanyIdentitySectionProps): JSX.Element {
  const { t } = useTranslation();
  const audienceKey = audience === "accountant" ? "Acct" : "";
  const { watch, setValue, control } = useFormContext<FiscalProfileEditorValues>();
  const companyType = watch("companyType");
  const hasEmployees = watch("hasEmployees");
  const paysFonasaServiciosPersonales = watch("paysFonasaServiciosPersonales");

  const industryLabel =
    industryCode?.trim() !== ""
      ? t(`onboarding.industryOptions.${industryCode}`, {
          defaultValue: industryCode ?? "",
        })
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-foreground">
          {t(
            `settings.fiscalProfile.sections.companyIdentityTitle${audienceKey}`,
          )}
        </CardTitle>
        <CardDescription>
          {t(
            `settings.fiscalProfile.sections.companyIdentityDescription${audienceKey}`,
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!hideCompanyType && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-foreground">
                {t("settings.fiscalProfile.companyType")}
              </Label>
              <Select
                value={companyType}
                onValueChange={(v) => setValue("companyType", v as FiscalCompanyType)}
              >
                <SelectTrigger className="transition-colors duration-150">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMPANY_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`settings.fiscalProfile.${COMPANY_TYPE_KEYS[type]}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {industryLabel ? (
          <div className="space-y-1">
            <Label className="text-foreground">
              {t("settings.fiscalProfile.sections.activityLabel")}
            </Label>
            <p className="text-sm text-muted-foreground">{industryLabel}</p>
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <div className="flex items-start space-x-2">
            <Checkbox
              id={`${idPrefix}-is-exporter`}
              checked={watch("isExportingServices")}
              onCheckedChange={(c) => setValue("isExportingServices", c === true)}
              className="mt-0.5 transition-colors duration-150"
            />
            <div className="grid gap-0.5">
              <Label
                htmlFor={`${idPrefix}-is-exporter`}
                className="cursor-pointer text-sm font-normal"
              >
                {t("settings.fiscalProfile.isExporter")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.fiscalProfile.isExporterHelp")}
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox
              id={`${idPrefix}-has-employees`}
              checked={hasEmployees}
              onCheckedChange={(c) => setValue("hasEmployees", c === true)}
              className="mt-0.5 transition-colors duration-150"
            />
            <div className="grid gap-0.5">
              <Label
                htmlFor={`${idPrefix}-has-employees`}
                className="cursor-pointer text-sm font-normal"
              >
                {t("settings.fiscalProfile.hasEmployees")}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.fiscalProfile.hasEmployeesHelp")}
              </p>
            </div>
          </div>
          {/*
            Profesionales que aportan a la Caja Profesional: at BPS they pay
            FONASA (servicios personales, near month end) and NOT the aporte
            jubilatorio. Unchecked means "not a profesional", which is also
            what an untouched profile means — the safe default, since turning
            it on suppresses the BPS aporte rows.
          */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id={`${idPrefix}-fonasa-servicios-personales`}
              checked={paysFonasaServiciosPersonales === true}
              onCheckedChange={(c) =>
                setValue("paysFonasaServiciosPersonales", c === true)
              }
              className="mt-0.5 transition-colors duration-150"
            />
            <div className="grid gap-0.5">
              <Label
                htmlFor={`${idPrefix}-fonasa-servicios-personales`}
                className="cursor-pointer text-sm font-normal"
              >
                {t(
                  "settings.fiscalProfile.paysFonasaServiciosPersonales",
                  "Aporta a Caja Profesional (paga FONASA, no BPS jubilatorio)",
                )}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t(
                  "settings.fiscalProfile.paysFonasaServiciosPersonalesHelp",
                  "Para profesionales (abogado, contador, escribano, inmobiliaria). Si lo marcás, la agenda muestra el FONASA de servicios personales (vence cerca de fin de mes) y la declaración anual de abril, en lugar del aporte jubilatorio de BPS.",
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Controller
              name="activityStartDate"
              control={control}
              render={({ field }) => (
                <DatePickerField
                  id={`${idPrefix}-activity-start-date`}
                  value={field.value}
                  onChange={field.onChange}
                  label={t("settings.fiscalProfile.activityStartDate")}
                />
              )}
            />
            <p className="text-xs text-muted-foreground">
              {t("settings.fiscalProfile.activityStartDateHelp")}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
